import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Card from 'components/Card';
import DataTable from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import PageHeaderIcon from 'components/PageHeaderIcon';
import QuickActions from 'components/QuickActions';
import SearchInput from 'components/SearchInput';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import { useGetAnnualPurchaseList } from '../gql/useGetAnnualPurchaseList';
import { useGetAnnualPurchaseReport } from '../gql/useGetAnnualPurchaseReport.mutation';
import {
  PageWrapper,
  Container,
  DashboardLayout,
  MainContent,
  Sidebar,
  HeaderContainer,
  HeaderTitle,
  HeaderSubtitle,
  ContentWrapper,
  PageHeader,
} from './StyledComponents';

// Receipt icon for page header
const ReceiptIcon = () => (
  <svg
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9h6m-6 4h6"
    />
  </svg>
);

const PurchaseDetailPageContent = () => {
  const location = useLocation();
  const { pathname, search } = location;
  const { id: ticketId } = useParams();

  // Get query params
  const searchParams = new URLSearchParams(search);
  const ticketName = searchParams.get('ticketName') || 'Annual Ticket';
  const accountName = searchParams.get('accountName') || '';
  const accountId = searchParams.get('account_id') || '';

  // Context-aware routing
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  // Export report hook
  const getAnnualPurchaseReport = useGetAnnualPurchaseReport(accountId);

  // Search and pagination
  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Fetch purchase data
  const { data, loading } = useGetAnnualPurchaseList();

  const purchases = data?.getAnnualPurchaseList?.results ?? [];
  const totalCount = data?.getAnnualPurchaseList?.count ?? 0;
  const totalPages = Math.ceil(totalCount / 15);

  // Handlers
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  const handleSearch = (searchTerm) => {
    updateQuery({ queryString: searchTerm, page: 1 });
  };

  // CSV Export handler
  const handleExportCSV = async () => {
    try {
      const userType = pathname?.split('/').includes('admin-employee')
        ? 'employee'
        : pathname?.split('/').includes('admin-track')
        ? 'admin-track'
        : 'admin';
      const isStaging = pathname.includes('-staging');

      const response = await getAnnualPurchaseReport();

      if (!response || response.errors) {
        return;
      }

      if (response.data.getAnnualPurchaseListV2) {
        const transactions = response.data.getAnnualPurchaseListV2;
        const moment = await import('moment');
        const _title = moment.default().format('MMM DD - YYYY ').toUpperCase();
        const filename = _title + ticketName + '- Annual Purchase List';
        const dateTimeStamp = moment.default().format('MMM DD - YYYY h:mm A');

        const headers = `Purchaser Last Name,Purchaser First Name,Order Total,Discount,Refund,Credit,Absorbed Fees,Transaction Total,Time/Date,Transaction Link${'\n'}`;

        const transactionRows = transactions.data.map((data) =>
          data.data
            .map((transaction) => {
              const {
                id,
                purchaser,
                credit,
                discount,
                total,
                refund,
                subtotal,
                fee,
                purchase_date,
              } = transaction;
              const linkUrl = `https://dashboard${
                isStaging ? '-staging' : ''
              }.${
                process.env.REACT_APP_PLATFORM
              }.com/${userType}/annualPurchases/purchase/${ticketId}?ticketName=${ticketName}&purchase_id=${id}`;
              return `"${purchaser?.split(',')[0]}","${
                purchaser?.split(',')[1]
              }","${subtotal}","${discount}","${refund}","${credit}","${fee}","${total}","${purchase_date}","${linkUrl}"`;
            })
            .join('\n')
        );

        const csv = `Annual Purchase List
        Exported:, ${dateTimeStamp}
        Ticket Name:, ${ticketName}
        Account Name:, ${accountName}
        ${
          transactionRows[1]
            ? `Credits Redeemed - ${transactions.data[1].total}\n${headers}${transactionRows[1]} \n`
            : `Credits Redeemed - ${transactions.data[1].total} \n`
        }
        ${
          transactionRows[2]
            ? `Credits Issued - ${transactions.data[2].total}\n${headers}${transactionRows[2]} \n`
            : `Credits Issued - ${transactions.data[2].total} \n`
        }
        ${
          transactionRows[4]
            ? `Refunds Issued - ${transactions.data[4].total}\n${headers}${transactionRows[4]} \n`
            : `Refunds Issued - ${transactions.data[4].total} \n`
        }
        ${
          transactionRows[3]
            ? `Discounts - ${transactions.data[3].total}\n${headers}${transactionRows[3]} \n`
            : `Discounts - ${transactions.data[3].total} \n`
        }
        ${
          transactionRows[0]
            ? `All Transactions - ${transactions.data[0].total}\n${headers}${transactionRows[0]},`
            : `All Transactions - ${transactions.data[0].total}`
        }
      `;

        const element = document.createElement('a');
        element.setAttribute(
          'href',
          'data:text/plain;charset=utf-8,' + encodeURIComponent(csv.trim())
        );
        element.setAttribute('download', `${filename}.csv`);
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
      }
    } catch (e) {
      console.error('Export error:', e);
    }
  };

  // PDF Export handler
  const handleExportPDF = async () => {
    try {
      const response = await getAnnualPurchaseReport();
      if (!response || response.errors) return;

      const annualPurchaseReport = response.data.getAnnualPurchaseListV2;
      const [moment, jsPDF, FileSaver] = await Promise.all([
        import('moment'),
        import('jspdf'),
        import('file-saver'),
      ]);
      await import('jspdf-autotable');

      const doc = new jsPDF.default('landscape', 'pt', 'A4', true);
      const title = moment
        .default()
        .format('MMM DD - YYYY h:mm A')
        .toUpperCase();
      const filename = 'Annual Purchase List - ' + title;

      doc.setFontSize(15);
      doc.text(annualPurchaseReport.label, 40, 60);
      doc.text(accountName, 40, 80);
      doc.text(`Total Sales ${annualPurchaseReport.gross_sales}`, 40, 100);

      const headers = [
        [
          'Date/Time',
          'Purchaser',
          'Order Total',
          'Discount',
          'Refund',
          'Credit',
          'Absorbed Fees',
          'Transaction Total',
        ],
      ];
      const columns = [
        { dataKey: 'date', header: 'Date/Time' },
        { dataKey: 'purchaser', header: 'Purchaser' },
        { dataKey: 'subtotal', header: 'Order Total' },
        { dataKey: 'discount', header: 'Discount' },
        { dataKey: 'refund', header: 'Refund' },
        { dataKey: 'credit', header: 'Credit' },
        { dataKey: 'fee', header: 'Absorbed Fees' },
        { dataKey: 'total', header: 'Transaction Total' },
      ];

      const Y = 140;

      // All Transactions section
      const allData =
        annualPurchaseReport?.data[0]?.data?.map((t) => [
          t.purchase_date,
          t.purchaser,
          t.subtotal,
          t.discount,
          t.refund || '',
          t.credit,
          t.fee,
          t.total,
        ]) || [];

      doc.text(
        `All Transactions - ${annualPurchaseReport?.data[0]?.total}`,
        40,
        Y
      );
      doc.autoTable({
        startY: Y + 10,
        columns,
        head: headers,
        body: allData,
        headStyles: { fillColor: '#fa4616' },
      });

      // Save
      const blob = doc.output('blob');
      FileSaver.saveAs(blob, `${filename}.pdf`);
    } catch (e) {
      console.error('PDF export error:', e);
    }
  };

  // Table columns — transaction data
  const columns = [
    {
      field: 'purchase_date',
      header: 'Date/Time',
      render: (row) => row.purchase_date || '—',
    },
    {
      field: 'purchaser',
      header: 'Purchaser Name',
      render: (row) => row.user?.formatted_name || `Transaction #${row.id}`,
    },
    {
      field: 'total',
      header: 'Order Total',
      render: (row) => row.total || '$0.00',
    },
    {
      field: 'discount',
      header: 'Discount',
      render: (row) => row.discount || '-$0.00',
    },
    {
      field: 'refund',
      header: 'Refund',
      render: (row) => row.refund || '-$0.00',
    },
    {
      field: 'credit',
      header: 'Credit',
      render: (row) => row.credit || '$0.00',
    },
    {
      field: 'fee',
      header: 'Fee',
      render: (row) => row.fee || '$0.00',
    },
    {
      field: 'subtotal',
      header: 'Transaction Total',
      render: (row) => row.subtotal || '$0.00',
    },
  ];

  // Table actions
  const actions = [
    {
      type: 'view',
      title: 'View Transaction',
      to: (row) => `${routePrefix}/transactions/detail/${row.id}`,
    },
  ];

  return (
    <PageWrapper>
      <Container>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon>
              <ReceiptIcon />
            </PageHeaderIcon>
            <div>
              <HeaderTitle>{decodeURIComponent(ticketName)}</HeaderTitle>
              {accountName && (
                <HeaderSubtitle>
                  {decodeURIComponent(accountName)} - {totalCount} transactions
                </HeaderSubtitle>
              )}
            </div>
          </HeaderContainer>
        </Card>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            {/* Data Table Card */}
            <Card noPadding>
              <ContentWrapper>
                {/* Search Row */}
                <PageHeader>
                  <SearchInput
                    onSearch={handleSearch}
                    placeholder="Search by last name..."
                  />
                </PageHeader>

                <DataTable
                  columns={columns}
                  data={purchases}
                  loading={loading}
                  keyField="id"
                  emptyMessage="No purchases found"
                  emptySubtext="No one has purchased this ticket yet"
                  emptyIcon={
                    <svg
                      width="48"
                      height="48"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ marginBottom: '16px', opacity: 0.5 }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9h6m-6 4h6"
                      />
                    </svg>
                  }
                  actions={actions}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </ContentWrapper>
            </Card>
          </MainContent>

          <Sidebar>
            <QuickActions
              title="Quick Actions"
              actions={[
                ...(totalCount > 0
                  ? [
                      {
                        label: 'Export CSV',
                        icon: (
                          <svg
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                          >
                            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                            <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                          </svg>
                        ),
                        onClick: handleExportCSV,
                      },
                      {
                        label: 'Export PDF',
                        icon: (
                          <svg
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                          >
                            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                            <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                          </svg>
                        ),
                        onClick: handleExportPDF,
                      },
                    ]
                  : []),
              ]}
            />
          </Sidebar>
        </DashboardLayout>
      </Container>
    </PageWrapper>
  );
};

// Wrap with theme provider
const PurchaseDetailPage = () => (
  <EnhancedThemeProvider>
    <PurchaseDetailPageContent />
  </EnhancedThemeProvider>
);

export default PurchaseDetailPage;
